import { apiRequest } from "@/lib/api";
import { getAccessToken } from "@/lib/auth-storage";

export type ProjectStatus =
  | "ACTIVE"
  | "ON_HOLD"
  | "COMPLETED"
  | "CANCELLED"
  | "ARCHIVED";

export type ProjectLabel = {
  id: string;
  name: string;
  color: string;
  createdAt: string;
  updatedAt: string;
};

export type ProjectItem = {
  id: string;
  name: string;
  code?: string | null;
  clientId?: string | null;
  jobSiteAddress?: string | null;
  status?: ProjectStatus;
  isActive: boolean;
  archivedAt?: string | null;
  _count?: {
    tickets?: number;
  };
  client?: {
    id: string;
    name: string;
    companyName?: string | null;
    type?: string | null;
    email?: string | null;
    phone?: string | null;
  } | null;
  assignedMember?: {
    id: string;
    fullName: string;
    email: string;
    role?: string;
  } | null;
  labels?: ProjectLabel[];
  approvedTotal?: number;
  approvedTicketCount?: number;
  pendingApprovalTicketCount?: number;
  latestApprovedTicketAt?: string | null;
  hasScheduleAfterLatestApproval?: boolean;
  paidTotal?: number;
  balance?: number;
  paymentStatus?: "NO_APPROVED_TOTAL" | "UNPAID" | "PARTIALLY_PAID" | "PAID";
  nextSchedule?: {
    id: string;
    startAt: string;
    endAt: string;
    durationMinutes?: number | null;
    status:
      | "SCHEDULED"
      | "IN_PROGRESS"
      | "PAUSED"
      | "DONE"
      | "CANCELED"
      | "MISSED";
  } | null;
  lastSchedule?: {
    id: string;
    startAt: string;
    endAt: string;
    durationMinutes?: number | null;
    status:
      | "SCHEDULED"
      | "IN_PROGRESS"
      | "PAUSED"
      | "DONE"
      | "CANCELED"
      | "MISSED";
  } | null;
};

export type ProjectPaymentMethod =
  | "CASH"
  | "CHECK"
  | "CARD"
  | "ZELLE"
  | "ACH"
  | "OTHER";

export type ProjectPaymentItem = {
  id: string;
  amount: number;
  paymentDate: string;
  method: ProjectPaymentMethod;
  note?: string | null;
  createdAt: string;
  createdBy: {
    id: string;
    fullName: string;
    email: string;
  };
};

export type ProjectPaymentsResponse = {
  project: {
    id: string;
    name: string;
  };
  items: ProjectPaymentItem[];
  totalPaid: number;
};

export type ProjectFinalInvoiceDetailLevel = "SUMMARY_ONLY" | "WITH_BREAKDOWNS";

export type ProjectFinalInvoiceSummary = {
  id: string;
  invoiceNumber: string;
  status: "GENERATED" | "SENT";
  detailLevel: ProjectFinalInvoiceDetailLevel;
  showPayments?: boolean;
  approvedTotal: number;
  paymentsReceived: number;
  balanceDue: number;
  sentAt?: string | null;
  sentToEmail?: string | null;
  sentById?: string | null;
  generatedAt: string;
  generatedBy?: {
    id: string;
    fullName: string;
    email: string;
  } | null;
};

export type FinalInvoiceContextTicket = {
  id: string;
  ticketNumber: string;
  type: "TM_TICKET" | "CHANGE_ORDER" | "EXTRA_WORK";
  typeLabel: string;
  title: string;
  approvedAt?: string | null;
  createdAt: string;
  amount: number;
};

export type FinalInvoiceContextResponse = {
  project: {
    id: string;
    name: string;
    client?: {
      name: string;
      companyName?: string | null;
      email?: string | null;
      phone?: string | null;
    } | null;
  };
  tickets: FinalInvoiceContextTicket[];
  summary: {
    approvedTotal: number;
    paymentsReceived: number;
    balanceDue: number;
  };
  latestInvoice?: ProjectFinalInvoiceSummary | null;
};

export type ClientItem = {
  id: string;
  name: string;
  companyName?: string | null;
  type?: string | null;
  email?: string | null;
  phone?: string | null;
  address?: string | null;
  _count?: {
    projects?: number;
    tickets?: number;
  };
};

export function fetchProjects(
  input: string | { search?: string; labelIds?: string[] } = "",
) {
  const search = typeof input === "string" ? input : (input.search ?? "");
  const labelIds = typeof input === "string" ? [] : (input.labelIds ?? []);
  const query = new URLSearchParams();
  if (search.trim()) query.set("search", search.trim());
  if (labelIds.length) query.set("labelIds", labelIds.join(","));
  return apiRequest<ProjectItem[]>(`/projects?${query.toString()}`, {
    method: "GET",
  });
}

export function fetchProjectById(id: string) {
  return apiRequest<ProjectItem & { tickets?: unknown[] }>(`/projects/${id}`, {
    method: "GET",
  });
}

export function createProject(payload: {
  name: string;
  code?: string;
  clientId: string;
  jobSiteAddress?: string;
  status?: ProjectStatus;
  labelIds?: string[];
}) {
  return apiRequest<ProjectItem>("/projects", {
    method: "POST",
    body: payload,
  });
}

export function updateProject(
  id: string,
  payload: {
    name?: string;
    code?: string;
    clientId?: string;
    jobSiteAddress?: string;
    isActive?: boolean;
    status?: ProjectStatus;
    labelIds?: string[];
    assignedMemberId?: string;
  },
) {
  return apiRequest<ProjectItem>(`/projects/${id}`, {
    method: "PATCH",
    body: payload,
  });
}

export function fetchProjectLabels() {
  return apiRequest<ProjectLabel[]>("/projects/labels", { method: "GET" });
}

export function createProjectLabel(payload: { name: string; color: string }) {
  return apiRequest<ProjectLabel>("/projects/labels", {
    method: "POST",
    body: payload,
  });
}

export function updateProjectLabel(
  id: string,
  payload: {
    name?: string;
    color?: string;
  },
) {
  return apiRequest<ProjectLabel>(`/projects/labels/${id}`, {
    method: "PATCH",
    body: payload,
  });
}

export function deleteProjectLabel(id: string) {
  return apiRequest<{ success: boolean }>(`/projects/labels/${id}`, {
    method: "DELETE",
  });
}

export async function archiveProjectAndDownload(
  id: string,
  payload: {
    finalStatus?: Exclude<ProjectStatus, "ARCHIVED">;
  },
) {
  const API_BASE =
    (import.meta.env.VITE_API_BASE_URL as string | undefined) ?? "/api";
  const token = getAccessToken();
  const response = await fetch(`${API_BASE}/projects/${id}/archive`, {
    method: "POST",
    headers: {
      "Content-Type": "application/json",
      ...(token ? { Authorization: `Bearer ${token}` } : {}),
    },
    body: JSON.stringify(payload),
  });

  if (!response.ok) {
    let message = `Request failed (${response.status})`;
    try {
      const error = await response.json();
      message = error?.message ?? error?.error ?? message;
    } catch {
      // no-op
    }
    throw new Error(Array.isArray(message) ? message.join(", ") : message);
  }

  const blob = await response.blob();
  const disposition = response.headers.get("Content-Disposition") || "";
  const match = disposition.match(/filename="?([^"]+)"?/i);
  const fileName = match?.[1] || "project-archive.pdf";
  return { blob, fileName };
}

export async function downloadArchivedProjectPdf(id: string) {
  const API_BASE =
    (import.meta.env.VITE_API_BASE_URL as string | undefined) ?? "/api";
  const token = getAccessToken();
  const response = await fetch(`${API_BASE}/projects/${id}/archive-pdf`, {
    method: "POST",
    headers: {
      ...(token ? { Authorization: `Bearer ${token}` } : {}),
    },
  });

  if (!response.ok) {
    let message = `Request failed (${response.status})`;
    try {
      const error = await response.json();
      message = error?.message ?? error?.error ?? message;
    } catch {
      // no-op
    }
    throw new Error(Array.isArray(message) ? message.join(", ") : message);
  }

  const blob = await response.blob();
  const disposition = response.headers.get("Content-Disposition") || "";
  const match = disposition.match(/filename="?([^"]+)"?/i);
  const fileName = match?.[1] || "project-archive.pdf";
  return { blob, fileName };
}

export function restoreArchivedProject(id: string) {
  return apiRequest<ProjectItem>(`/projects/${id}/restore`, { method: "POST" });
}

export function deleteProject(id: string) {
  return apiRequest<{ success: boolean }>(`/projects/${id}`, {
    method: "DELETE",
  });
}

export function fetchProjectPayments(projectId: string) {
  return apiRequest<ProjectPaymentsResponse>(
    `/projects/${projectId}/payments`,
    {
      method: "GET",
    },
  );
}

export function fetchFinalInvoiceContext(projectId: string) {
  return apiRequest<FinalInvoiceContextResponse>(
    `/projects/${projectId}/final-invoices/context`,
    {
      method: "GET",
    },
  );
}

export function generateFinalInvoice(
  projectId: string,
  payload: {
    ticketIds: string[];
    detailLevel: ProjectFinalInvoiceDetailLevel;
    invoiceNote?: string;
    showPayments?: boolean;
  },
) {
  return apiRequest<ProjectFinalInvoiceSummary>(
    `/projects/${projectId}/final-invoices`,
    {
      method: "POST",
      body: payload,
    },
  );
}

async function fetchProjectPdfBlob(url: string, fallbackFileName: string) {
  const API_BASE =
    (import.meta.env.VITE_API_BASE_URL as string | undefined) ?? "/api";
  const token = getAccessToken();
  const response = await fetch(`${API_BASE}${url}`, {
    method: "GET",
    headers: {
      ...(token ? { Authorization: `Bearer ${token}` } : {}),
    },
  });

  if (!response.ok) {
    let message = `Request failed (${response.status})`;
    try {
      const error = await response.json();
      message = error?.message ?? error?.error ?? message;
    } catch {
      // no-op
    }
    throw new Error(Array.isArray(message) ? message.join(", ") : message);
  }

  const blob = await response.blob();
  const disposition = response.headers.get("Content-Disposition") || "";
  const match = disposition.match(/filename="?([^"]+)"?/i);
  const fileName = match?.[1] || fallbackFileName;
  return { blob, fileName };
}

export function viewFinalInvoicePdf(projectId: string, invoiceId: string) {
  return fetchProjectPdfBlob(
    `/projects/${projectId}/final-invoices/${invoiceId}/pdf`,
    "final-invoice.pdf",
  );
}

export function downloadFinalInvoicePdf(projectId: string, invoiceId: string) {
  return fetchProjectPdfBlob(
    `/projects/${projectId}/final-invoices/${invoiceId}/download`,
    "final-invoice.pdf",
  );
}

export function sendFinalInvoiceToClient(projectId: string, invoiceId: string) {
  return apiRequest<ProjectFinalInvoiceSummary>(
    `/projects/${projectId}/final-invoices/${invoiceId}/send`,
    {
      method: "POST",
    },
  );
}

export function createProjectPayment(
  projectId: string,
  payload: {
    amount: number;
    paymentDate: string;
    method: ProjectPaymentMethod;
    note?: string;
  },
) {
  return apiRequest<ProjectPaymentItem>(`/projects/${projectId}/payments`, {
    method: "POST",
    body: payload,
  });
}

export function deleteProjectPayment(projectId: string, paymentId: string) {
  return apiRequest<{ success: boolean }>(
    `/projects/${projectId}/payments/${paymentId}`,
    {
      method: "DELETE",
    },
  );
}

export function updateProjectPayment(
  projectId: string,
  paymentId: string,
  payload: {
    amount?: number;
    paymentDate?: string;
    method?: ProjectPaymentMethod;
    note?: string;
  },
) {
  return apiRequest<ProjectPaymentItem>(
    `/projects/${projectId}/payments/${paymentId}`,
    {
      method: "PATCH",
      body: payload,
    },
  );
}

export function fetchClients(search = "") {
  const query = new URLSearchParams();
  if (search.trim()) query.set("search", search.trim());
  return apiRequest<ClientItem[]>(`/clients?${query.toString()}`, {
    method: "GET",
  });
}

export function fetchClientById(id: string) {
  return apiRequest<ClientItem & { projects?: unknown[]; tickets?: unknown[] }>(
    `/clients/${id}`,
    {
      method: "GET",
    },
  );
}

export function createClient(payload: {
  name: string;
  companyName?: string;
  type?: string;
  email?: string;
  phone?: string;
  address?: string;
}) {
  return apiRequest<ClientItem>("/clients", { method: "POST", body: payload });
}

export function updateClient(
  id: string,
  payload: {
    name?: string;
    companyName?: string;
    type?: string;
    email?: string;
    phone?: string;
    address?: string;
  },
) {
  return apiRequest<ClientItem>(`/clients/${id}`, {
    method: "PATCH",
    body: payload,
  });
}

export function deleteClient(id: string) {
  return apiRequest<{ success: boolean }>(`/clients/${id}`, {
    method: "DELETE",
  });
}
